import api from './api';
import { getSessionId as getBridgeSessionId, isWebView } from './webviewService';

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop()?.split(';').shift() || null : null;
};

const setCookie = (name: string, value: string): void => {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${10 * 365 * 24 * 60 * 60}; Path=/; SameSite=Lax`;
};

const createId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ai-agent-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getDeviceId = (): string => {
  const existing = getCookie('deviceId');
  if (existing) return existing;
  const deviceId = createId();
  setCookie('deviceId', deviceId);
  return deviceId;
};

const getSessionId = (): string => {
  let sessionId = getBridgeSessionId();

  // Some WebView bridges return a JSON string rather than the ID itself.
  if (sessionId.startsWith('{')) {
    try {
      const parsed = JSON.parse(sessionId) as { sessionId?: string; session_id?: string };
      sessionId = parsed.sessionId || parsed.session_id || '';
    } catch {
      sessionId = '';
    }
  }

  if (sessionId.length >= 8) return sessionId;

  const cookieSessionId = getCookie('khabarchin-session-key');
  if (cookieSessionId && cookieSessionId.length >= 8) return cookieSessionId;

  const newSessionId = createId();
  setCookie('khabarchin-session-key', newSessionId);
  return newSessionId;
};

const getPlatform = (): string => (isWebView() ? 'android' : 'web');

/** Kept as a compatibility hook; event delivery is owned by the backend. */
export const initializeEventTracking = (_userId = 0): void => undefined;

const postEvent = async (path: string, payload: Record<string, unknown>): Promise<void> => {
  try {
    // The API acknowledges after the event is durably queued in Redis. Khabarchin
    // and Kafka/Postgres delivery happens asynchronously in the worker.
    await api.post(path, {
      ...payload,
      device_id: getDeviceId(),
      khabarchin_session_id: getSessionId(),
      platform: getPlatform(),
    });
  } catch (error) {
    // Telemetry must never block chat navigation or user actions.
    console.warn('AI-agent event enqueue failed', path, error);
  }
};

export const eventService = {
  captureProductClick(
    agent_chat_id: number,
    product_id: number,
    agent_click_type: string,
    agent_type?: string | null,
  ): Promise<void> {
    return postEvent('/api/events/capture/product-click', {
      agent_chat_id,
      product_id,
      agent_click_type,
      agent_type: agent_type || null,
    });
  },

  captureHomePageView(agent_type?: string | null): Promise<void> {
    return postEvent('/api/events/capture/home-page-view', { agent_type: agent_type || null });
  },

  captureSubmitMessage(
    agent_chat_id: number,
    user_message: string,
    agent_type?: string | null,
  ): Promise<void> {
    return postEvent('/api/events/capture/submit-message', {
      agent_chat_id,
      user_message,
      agent_type: agent_type || null,
    });
  },

  captureAgentResponse(
    agent_chat_id: number,
    agent_message_id: number,
    agent_type?: string | null,
  ): Promise<void> {
    return postEvent('/api/events/capture/agent-response', {
      agent_chat_id,
      agent_message_id,
      agent_type: agent_type || null,
    });
  },
};
